/**
 * Observable application state management
 * @module state
 */

/**
 * @typedef {'idle' | 'syncing' | 'error' | 'auth-error'} SyncStatus
 */

/**
 * @typedef {Object} AppStateData
 * @property {Object|null} user - Current authenticated user
 * @property {Object|null} config - Application configuration from server
 * @property {SyncStatus} syncStatus - Current synchronization status
 * @property {number} pendingCount - Number of items awaiting sync
 * @property {number|null} lastSyncTime - Timestamp of last successful sync
 * @property {boolean} isOnline - Network connectivity status
 */

class AppState {
  constructor() {
    /** @type {AppStateData} */
    this._state = {
      user: null,
      config: null,
      syncStatus: 'idle',
      pendingCount: 0,
      lastSyncTime: null,
      isOnline: typeof navigator !== 'undefined' ? navigator.onLine : true
    };

    /** @type {Map<string, Set<Function>>} */
    this._listeners = new Map();

    this._initNetworkListeners();
  }

  /**
   * Bind network status event listeners
   * @private
   */
  _initNetworkListeners() {
    if (typeof window === 'undefined') return;

    window.addEventListener('online', () => {
      this.set('isOnline', true);
    });

    window.addEventListener('offline', () => {
      this.set('isOnline', false);
    });
  }

  /**
   * Retrieve a state value
   * @param {keyof AppStateData} key - State key
   * @returns {*} Current value
   */
  get(key) {
    return this._state[key];
  }

  /**
   * Update a state value and notify subscribers
   * @param {keyof AppStateData} key - State key
   * @param {*} value - New value
   */
  set(key, value) {
    const oldValue = this._state[key];
    if (oldValue === value) return;

    this._state[key] = value;
    this._notify(key, value, oldValue);
  }

  /**
   * Subscribe to state changes for a specific key
   * @param {keyof AppStateData} key - State key to observe
   * @param {Function} callback - Callback receiving (newValue, oldValue)
   * @returns {Function} Unsubscribe function
   */
  subscribe(key, callback) {
    if (!this._listeners.has(key)) {
      this._listeners.set(key, new Set());
    }
    this._listeners.get(key).add(callback);

    // Return unsubscribe function
    return () => {
      const keyListeners = this._listeners.get(key);
      if (keyListeners) {
        keyListeners.delete(callback);
        if (keyListeners.size === 0) {
          this._listeners.delete(key);
        }
      }
    };
  }

  /**
   * Notify subscribers of a state change
   * @private
   * @param {string} key - Changed key
   * @param {*} value - New value
   * @param {*} oldValue - Previous value
   */
  _notify(key, value, oldValue) {
    const keyListeners = this._listeners.get(key);
    if (!keyListeners) return;

    for (const callback of keyListeners) {
      try {
        callback(value, oldValue);
      } catch (error) {
        console.error(`[State] Subscriber error for key "${key}":`, error);
      }
    }
  }

  /**
   * Get a snapshot of the entire state (read-only)
   * @returns {Readonly<AppStateData>}
   */
  getSnapshot() {
    return { ...this._state };
  }
}

/** Singleton application state instance */
export const appState = new AppState();
